import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import ArrowLeftIcon from '../../components/icons/ArrowLeftIcon';
import { useAuth } from '../../contexts/AuthContext';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import TextInput from '../../components/FormElements/TextInput';
import SelectInput from '../../components/FormElements/SelectInput';

const FAQManagerFormAdminScreen = () => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { faqId } = useParams(); // For potential edit mode in the future
    const isEditMode = Boolean(faqId);
    const { token } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        question: '',
        answer: '',
        role: 'user', // Default role
        display_order: 0,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false); // For edit mode loading
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
    const alertRef = useRef(null);

    const roleOptions = [
        { value: 'user', label: 'Cliente' },
        { value: 'agent', label: 'Asesor' },
        { value: 'supplier', label: 'Proveedor' },
        { value: 'admin', label: 'Administrador' },
    ];

    // useEffect for fetching FAQ data if in edit mode (future enhancement)
    useEffect(() => {
        if (isEditMode && faqId && token) {
            const fetchFaqDetails = async () => {
                setIsLoadingData(true);
                setSubmitMessage({ type: '', text: '' });
                try {
                    const response = await fetch(`${API_BASE_URL}/api/faqs/${faqId}`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                    });
                    console.log("Response:", response)
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({ message: `Error fetching FAQ details: ${response.status}` }));
                        throw new Error(errorData.message || `Error fetching FAQ details: ${response.status}`);
                    }
                    const data = await response.json();
                    setFormData({
                        question: data.question || '',
                        answer: data.answer || '',
                        role: data.role || 'user',
                        display_order: data.display_order !== undefined ? data.display_order : 0,
                    });
                } catch (error) {
                    console.error("Error fetching FAQ details:", error);
                    setSubmitMessage({ type: 'danger', text: `Error al cargar los detalles de la P+F: ${error.message}` });
                } finally {
                    setIsLoadingData(false);
                }
            };
            fetchFaqDetails();
        }
    }, [isEditMode, faqId, API_BASE_URL, token]);


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitFAQ = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage({ type: '', text: '' });

        if (!formData.question.trim() || !formData.answer.trim()) {
            setSubmitMessage({ type: 'danger', text: 'La pregunta y la respuesta no pueden estar vacías.' });
            setIsSubmitting(false);
            return;
        }

        const payload = {
            question: formData.question,
            answer: formData.answer,
            role: formData.role,
            display_order: parseInt(formData.display_order, 10) || 0,
        };

        try {
            const url = isEditMode ? `${API_BASE_URL}/faqs/${faqId}` : `${API_BASE_URL}/faqs/`;
            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Error del servidor: ${response.status}` }));
                throw new Error(errorData.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} la P+F: ${response.statusText}`);
            }

            const responseData = await response.json();
            setSubmitMessage({ type: 'success', text: `¡P+F ${isEditMode ? 'actualizada' : 'guardada'} con éxito!` });

            if (!isEditMode) {
                // Clear form or redirect
                setTimeout(() => {
                    navigate('/settings/faqs'); // Redirect to the list after adding
                }, 1500);
            } else {
                // Optionally refetch data or update local state if needed after edit
            }

        } catch (error) {
            console.error("Error submitting FAQ:", error);
            setSubmitMessage({ type: 'danger', text: error.message || `No se pudo ${isEditMode ? 'actualizar' : 'guardar'} la P+F. Intente nuevamente.` });
        } finally {
            // For new FAQ, spinner might be turned off by navigation timeout
            // For edit, turn it off here
            if (isEditMode) {
                setIsSubmitting(false);
            } else {
                // Let the timeout handle spinner for new entries if redirecting
                // If not redirecting, set isSubmitting(false) here.
            }
        }
    };

    useEffect(() => {
        if (submitMessage.text && alertRef.current) {
            alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [submitMessage.text]);

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <div className="d-flex align-items-center">
                    <Link to="/settings/faqs" className="btn btn-back-circle me-3" title="Volver a P+F">
                        <ArrowLeftIcon />
                    </Link>
                    <h1 className="h2 mb-0">{isEditMode ? 'Editar Pregunta Frecuente' : 'Añadir Nueva Pregunta Frecuente'}</h1>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 text-start">
                        <FormSpinnerOverlay isLoading={isLoadingData || isSubmitting} />
                        <div className="card-body" style={{ opacity: (isLoadingData || isSubmitting) ? 0.7 : 1 }}>
                            <div ref={alertRef} style={{ minHeight: '60px' }}>
                                {submitMessage.text && <AlertMessage type={submitMessage.type} message={submitMessage.text} />}
                            </div>
                            <form onSubmit={handleSubmitFAQ}>
                                <TextInput label="Pregunta" id="faqQuestion" name="question" value={formData.question} onChange={handleInputChange} placeholder="Ingrese la pregunta" required />
                                <TextInput label="Respuesta" id="faqAnswer" name="answer" type="textarea" value={formData.answer} onChange={handleInputChange} rows="5" placeholder="Ingrese la respuesta" required />
                                <SelectInput label="Rol (Visible para)" id="faqRole" name="role" value={formData.role} onChange={handleInputChange} options={roleOptions} required />
                                <input type="hidden" name="display_order" value={formData.display_order} />
                                <div className="mt-4">
                                    <button type="submit" className="btn btn-checkin px-4" disabled={isSubmitting || isLoadingData}>
                                        {isEditMode ? 'Actualizar P+F' : 'Guardar P+F'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FAQManagerFormAdminScreen;